import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  User, Calendar, MapPin, Phone, Mail, Lock, Building, 
  Link, CheckSquare, Globe, MessageSquare, Info, Award,
  Check, Upload, Clock, AlertTriangle, Save
} from 'lucide-react';
import LocationAutocomplete from '../../../components/LocationAutocomplete';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const RegisterConstructoraTab = ({ 
  isLoading, 
  setIsLoading, 
  currentUser, 
  showNotification, 
  logAction
}) => {
  // Servicios disponibles para constructoras
  const SERVICIOS_CONSTRUCCION = [
    'Construcción residencial',
    'Construcción comercial',
    'Proyectos industriales',
    'Remodelaciones',
    'Diseño arquitectónico',
    'Gestión de proyectos',
    'Consultoría técnica',
    'Acabados de lujo',
    'Servicios de excavación',
    'Instalaciones especializadas'
  ];

  // Estado del formulario
  const [formData, setFormData] = useState({
    userType: 'constructora',
    nombre: '',
    nombre_empresa: '',
    slug: '',
    ruc: '',
    fechaNacimiento: '',
    fotoPerfil: null,
    fotoPreview: null,
    ciudad: '',
    provincia: '',
    pais: '',
    email: '',
    telefono: '',
    prefijo_telefono: '+51',
    whatsapp: '',
    facebook: '',
    instagram: '',
    sitio_web: '',
    sobre_nosotros: '',
    experiencia: '',
    anos_experiencia: '',
    servicios: [],
    horario_atencion: {
      lunes_viernes: { inicio: '', fin: '' },
      sabado: { inicio: '', fin: '' },
      domingo: { inicio: '', fin: '' }
    },
    direccion_sede: '',
    lat_sede: null,
    lng_sede: null,
    password: '',
    slogan: ''
  });

  // Estado de errores
  const [errors, setErrors] = useState({});
  const [registroExitoso, setRegistroExitoso] = useState(false);

  // Manejador de cambios
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    
    // Para el slug, eliminar caracteres especiales y espacios
    if (name === 'slug') {
      const slugValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: slugValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Limpiar errores al cambiar el valor
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Validaciones específicas
    switch (name) {
      case 'email':
        try {
          const response = await axios.get(`/api/admin/check-email/${value.trim()}`);
          if (response.data.exists) {
            setErrors(prev => ({
              ...prev,
              email: 'Este email ya está registrado'
            }));
          }
        } catch (error) {
          console.error('Error al verificar email:', error);
        }
        break;

      case 'telefono':
        if (value && value.length === 9) {
          try {
            const response = await axios.post('/api/admin/check-phone', {
              telefono: value,
              prefijo: formData.prefijo_telefono
            });
            
            if (!response.data.available) {
              setErrors(prev => ({
                ...prev,
                telefono: 'Este teléfono ya está registrado'
              }));
            }
          } catch (error) {
            console.error('Error al verificar teléfono:', error);
          }
        }
        break;

      case 'ruc':
        // Validar RUC peruano (11 dígitos)
        if (value && !/^\d{11}$/.test(value)) {
          setErrors(prev => ({
            ...prev,
            ruc: 'El RUC debe tener 11 dígitos'
          }));
        }
        break;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, fotoPerfil: 'La imagen no debe superar 5MB' }));
        return;
      }
      
      // Mostrar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ 
          ...prev, 
          fotoPerfil: file,
          fotoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
      
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.fotoPerfil;
        return newErrors;
      });
    }
  };

  const handleScheduleChange = (day, type, value) => {
    setFormData(prev => ({
      ...prev,
      horario_atencion: {
        ...prev.horario_atencion,
        [day]: {
          ...prev.horario_atencion[day],
          [type]: value
        }
      }
    }));
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      ubicacion: `${location.city}, ${location.province}, ${location.country}`,
      ciudad: location.city,
      provincia: location.province,
      pais: location.country
    }));
    
    // Limpiar errores de ubicación si existían
    if (errors.location) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.location;
        return newErrors;
      });
    }
  };

  const handleSedeLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      direccion_sede: location.formatted_address,
      lat_sede: location.lat,
      lng_sede: location.lng
    }));
    
    if (errors.sede) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sede;
        return newErrors;
      });
    }
  };

  const handleServiceChange = (service) => {
    setFormData(prev => {
      const newServices = prev.servicios.includes(service)
        ? prev.servicios.filter(s => s !== service)
        : [...prev.servicios, service];
      return { ...prev, servicios: newServices };
    });
  };

  // Validar formulario completo
  const validateForm = () => {
    const newErrors = {};

    // Validaciones de campos obligatorios
    if (!formData.email) newErrors.email = 'El email es requerido';
    if (!formData.password) newErrors.password = 'La contraseña es requerida';
    if (!formData.nombre) newErrors.nombre = 'El nombre es requerido';
    if (!formData.nombre_empresa) newErrors.nombre_empresa = 'La razón social es requerida';
    if (!formData.ruc) {
      newErrors.ruc = 'El RUC es requerido';
    } else if (!/^\d{11}$/.test(formData.ruc)) {
      newErrors.ruc = 'El RUC debe tener 11 dígitos';
    }
    if (!formData.slug) newErrors.slug = 'El identificador único es requerido';
    if (!formData.ciudad || !formData.provincia || !formData.pais) {
      newErrors.location = 'La ubicación es requerida';
    }
    if (formData.servicios.length === 0) {
      newErrors.servicios = 'Debe seleccionar al menos un servicio';
    }
    if (formData.servicios.length > 6) {
      newErrors.servicios = 'No puede seleccionar más de 6 servicios';
    }
    
    // Validación de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    // Validación de contraseña segura
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showNotification('Por favor, corrija los errores antes de continuar', 'error');
      // Hacer scroll al primer error
      const firstError = document.querySelector('.text-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsLoading(true);
    try {
      const formDataToSend = new FormData();
      
      // Preparar los datos para enviar
      Object.keys(formData).forEach(key => {
        if (key === 'fotoPerfil' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else if (key === 'horario_atencion') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (Array.isArray(formData[key])) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (key !== 'fotoPreview') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Registro administrativo (sin verificaciones)
      formDataToSend.append('adminRegistration', 'true');
      formDataToSend.append('registeredBy', currentUser.id);

      const response = await axios.post('/api/admin/register-constructora', formDataToSend);

      // Registrar acción
      logAction(
        'CONSTRUCTORA_REGISTRADA',
        `Se ha registrado la constructora ${formData.nombre} con RUC ${formData.ruc}`,
        response.data.constructoraId,
        'constructora'
      );
      
      showNotification('Constructora registrada correctamente', 'success');
      setRegistroExitoso(true);
      
      // Reiniciar formulario
      setFormData({
        userType: 'constructora',
        nombre: '',
        nombre_empresa: '',
        slug: '',
        ruc: '',
        fechaNacimiento: '',
        fotoPerfil: null,
        fotoPreview: null,
        ciudad: '',
        provincia: '',
        pais: '',
        email: '',
        telefono: '',
        prefijo_telefono: '+51',
        whatsapp: '',
        facebook: '',
        instagram: '',
        sitio_web: '',
        sobre_nosotros: '',
        experiencia: '',
        anos_experiencia: '',
        servicios: [],
        horario_atencion: {
          lunes_viernes: { inicio: '', fin: '' },
          sabado: { inicio: '', fin: '' },
          domingo: { inicio: '', fin: '' }
        },
        direccion_sede: '',
        lat_sede: null,
        lng_sede: null,
        password: '',
        slogan: ''
      });

      // Después de 3 segundos, quitar el mensaje de éxito
      setTimeout(() => {
        setRegistroExitoso(false);
      }, 3000);

    } catch (error) {
      console.error('Error en registro:', error);
      
      const errorMessage = error.response?.data?.message || 'Error al registrar constructora';
      showNotification(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Componente principal
  return (
    <motion.div 
      className="bg-white rounded-xl shadow-lg overflow-hidden"
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
    >
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Building className="w-5 h-5 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-800">Registro de Constructora</h2>
        </div>
      </div>

      <div className="p-6">
        {registroExitoso && (
          <div className="mb-6 bg-green-50 text-green-700 p-4 rounded-lg border border-green-200 flex items-center gap-3">
            <Check className="w-5 h-5 text-green-500" />
            <span>¡Constructora registrada correctamente!</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Sección: Información Básica */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-800 border-b border-gray-200 pb-2">
              Información Básica
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Nombre Comercial *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="text"
                    name="nombre"
                    placeholder="Nombre comercial"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-2 border ${errors.nombre ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  {errors.nombre && <p className="mt-1 text-sm text-red-500">{errors.nombre}</p>}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Razón Social *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="text"
                    name="nombre_empresa"
                    placeholder="Razón social completa"
                    value={formData.nombre_empresa}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-2 border ${errors.nombre_empresa ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  {errors.nombre_empresa && <p className="mt-1 text-sm text-red-500">{errors.nombre_empresa}</p>}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  RUC *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="text"
                    name="ruc"
                    placeholder="RUC (11 dígitos)"
                    value={formData.ruc}
                    onChange={handleInputChange}
                    maxLength="11"
                    className={`w-full pl-10 pr-4 py-2 border ${errors.ruc ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  {errors.ruc && <p className="mt-1 text-sm text-red-500">{errors.ruc}</p>}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Identificador Único *
                </label>
                <div className="relative">
                  <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="text"
                    name="slug"
                    placeholder="ID único para URL (sin espacios ni caracteres especiales)"
                    value={formData.slug}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-2 border ${errors.slug ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  {errors.slug && <p className="mt-1 text-sm text-red-500">{errors.slug}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    URL: constructora/{formData.slug}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Fecha de Fundación
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="date"
                    name="fechaNacimiento"
                    value={formData.fechaNacimiento}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Ubicación Principal *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <LocationAutocomplete 
                    onLocationSelect={handleLocationSelect}
                    placeholder="Ciudad, Provincia, País"
                    className={`w-full pl-10 pr-4 py-2 border ${errors.location ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  {errors.location && <p className="mt-1 text-sm text-red-500">{errors.location}</p>}
                </div>
                {(formData.ciudad || formData.provincia || formData.pais) && (
                  <div className="p-2 bg-gray-50 rounded-lg text-sm text-gray-700">
                    {[formData.ciudad, formData.provincia, formData.pais].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Sección: Credenciales */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-800 border-b border-gray-200 pb-2">
              Credenciales de Acceso
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email corporativo"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-2 border ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Contraseña *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="password"
                    name="password"
                    placeholder="Contraseña"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-2 border ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    Mínimo 8 caracteres
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sección: Contacto */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-800 border-b border-gray-200 pb-2">
              Información de Contacto
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Teléfono
                </label>
                <div className="flex">
                  <select
                    value={formData.prefijo_telefono}
                    onChange={(e) => setFormData(prev => ({ ...prev, prefijo_telefono: e.target.value }))}
                    className="pl-2 pr-1 py-2 border border-r-0 border-gray-300 rounded-l-lg bg-white"
                  >
                    <option value="+51">🇵🇪 +51</option>
                    <option value="+56">🇨🇱 +56</option>
                    <option value="+57">🇨🇴 +57</option>
                    <option value="+54">🇦🇷 +54</option>
                    <option value="+593">🇪🇨 +593</option>
                  </select>
                  <input
                    type="tel"
                    name="telefono"
                    placeholder="Teléfono de contacto"
                    value={formData.telefono}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setFormData(prev => ({ ...prev, telefono: value }));
                    }}
                    className={`w-full pl-4 pr-4 py-2 border ${errors.telefono ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-r-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                    maxLength="9"
                  />
                </div>
                {errors.telefono && <p className="mt-1 text-sm text-red-500">{errors.telefono}</p>}
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  WhatsApp
                </label>
                <div className="flex">
                  <div className="pl-3 pr-2 py-2 border border-r-0 border-gray-300 rounded-l-lg bg-white text-gray-600 flex items-center">
                    +51
                  </div>
                  <input
                    type="tel"
                    name="whatsapp"
                    placeholder="WhatsApp de ventas"
                    value={formData.whatsapp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setFormData(prev => ({ ...prev, whatsapp: value }));
                    }}
                    className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    maxLength="9"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Sitio Web
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="url"
                    name="sitio_web"
                    placeholder="https://www.ejemplo.com"
                    value={formData.sitio_web}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Redes Sociales
                </label>
                <div className="space-y-3">
                  <div className="flex">
                    <div className="pl-3 pr-2 py-2 border border-r-0 border-gray-300 rounded-l-lg bg-white text-gray-600 text-sm w-24 flex items-center">
                      Facebook
                    </div>
                    <input
                      type="text"
                      name="facebook"
                      placeholder="Usuario de Facebook"
                      value={formData.facebook}
                      onChange={handleInputChange}
                      className="flex-1 pl-4 pr-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="flex">
                    <div className="pl-3 pr-2 py-2 border border-r-0 border-gray-300 rounded-l-lg bg-white text-gray-600 text-sm w-24 flex items-center">
                      Instagram
                    </div>
                    <input
                      type="text"
                      name="instagram"
                      placeholder="Usuario de Instagram"
                      value={formData.instagram}
                      onChange={handleInputChange}
                      className="flex-1 pl-4 pr-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sección: Servicios y Experiencia */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-800 border-b border-gray-200 pb-2">
              Servicios y Experiencia
            </h3>
            
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Slogan
                </label>
                <input
                  type="text"
                  name="slogan"
                  placeholder="Slogan o lema de la constructora"
                  value={formData.slogan}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Años de Experiencia
                </label>
                <input
                  type="number"
                  name="anos_experiencia"
                  placeholder="Años de experiencia en el sector"
                  value={formData.anos_experiencia}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Sobre la Constructora
                </label>
                <textarea
                  name="sobre_nosotros"
                  placeholder="Descripción de la empresa, historia, valores..."
                  value={formData.sobre_nosotros}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg min-h-[100px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                ></textarea>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Descripción de Experiencia
                </label>
                <textarea
                  name="experiencia"
                  placeholder="Detalle los proyectos más significativos, especialidades técnicas..."
                  value={formData.experiencia}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg min-h-[100px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                ></textarea>
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Servicios Ofrecidos * <span className="text-sm font-normal text-gray-500">(Máx. 6)</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {SERVICIOS_CONSTRUCCION.map((servicio) => (
                    <label
                      key={servicio}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.servicios.includes(servicio) 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.servicios.includes(servicio)}
                        onChange={() => handleServiceChange(servicio)}
                        className="sr-only"
                      />
                      <CheckSquare
                        className={`w-5 h-5 mr-2 ${
                          formData.servicios.includes(servicio) 
                            ? 'text-indigo-600' 
                            : 'text-gray-400'
                        }`}
                      />
                      <span className="text-sm">{servicio}</span>
                    </label>
                  ))}
                </div>
                {errors.servicios && <p className="mt-1 text-sm text-red-500">{errors.servicios}</p>}
              </div>
            </div>
          </div>
          
          {/* Sección: Horarios y Logo */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-800 border-b border-gray-200 pb-2">
              Horarios y Logo
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Horarios de Atención
                </label>
                
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="block text-xs text-gray-500">Lunes a Viernes</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={formData.horario_atencion.lunes_viernes.inicio}
                        onChange={(e) => handleScheduleChange('lunes_viernes', 'inicio', e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded-lg"
                      />
                      <span className="text-gray-500">a</span>
                      <input
                        type="time"
                        value={formData.horario_atencion.lunes_viernes.fin}
                        onChange={(e) => handleScheduleChange('lunes_viernes', 'fin', e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs text-gray-500">Sábado</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={formData.horario_atencion.sabado.inicio}
                        onChange={(e) => handleScheduleChange('sabado', 'inicio', e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded-lg"
                      />
                      <span className="text-gray-500">a</span>
                      <input
                        type="time"
                        value={formData.horario_atencion.sabado.fin}
                        onChange={(e) => handleScheduleChange('sabado', 'fin', e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-xs text-gray-500">Domingo</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={formData.horario_atencion.domingo.inicio}
                        onChange={(e) => handleScheduleChange('domingo', 'inicio', e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded-lg"
                      />
                      <span className="text-gray-500">a</span>
                      <input
                        type="time"
                        value={formData.horario_atencion.domingo.fin}
                        onChange={(e) => handleScheduleChange('domingo', 'fin', e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Logo de la Constructora
                </label>
                <div className="flex items-center gap-4">
                  <div className={`h-24 w-24 border-2 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50 ${formData.fotoPreview ? 'border-indigo-300' : 'border-gray-300'}`}>
                    {formData.fotoPreview ? (
                      <img src={formData.fotoPreview} alt="Vista previa" className="h-full w-full object-contain" />
                    ) : (
                      <Upload className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <label className="flex flex-col">
                      <div className="py-2 px-4 bg-indigo-50 text-indigo-600 rounded-lg border border-indigo-200 hover:bg-indigo-100 transition cursor-pointer text-sm">
                        {formData.fotoPerfil ? 'Cambiar logo' : 'Subir logo'}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <span className="mt-1 text-xs text-gray-500">
                        Formatos: JPG, PNG. Máx. 5MB
                      </span>
                    </label>
                    {errors.fotoPerfil && <p className="mt-1 text-sm text-red-500">{errors.fotoPerfil}</p>}
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Dirección de la Sede
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
                  <input
                    type="text"
                    name="direccion_sede"
                    placeholder="Dirección física de la sede"
                    value={formData.direccion_sede}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Mensaje de requisitos */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Información importante</p>
              <p>Los campos marcados con * son obligatorios.</p>
              <p>Al registrar la constructora, se creará automáticamente una cuenta para el usuario con el email y contraseña proporcionados.</p>
            </div>
          </div>
          
          {/* Botón de envío */}
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors ${
                isLoading 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md hover:shadow-lg'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  <span>Procesando...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Registrar Constructora</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default RegisterConstructoraTab;